import { useEffect, useLayoutEffect, useRef } from 'react';
import type { TFunction } from 'i18next';
import clsx from 'clsx';

export interface ContextMenuItem {
  action: string;
  label: string;
  disabled?: boolean;
  danger?: boolean;
  separatorBefore?: boolean;
}

export interface ContextMenuState {
  x: number;
  y: number;
}

interface DriveContextMenuProps {
  state: ContextMenuState | null;
  items: ContextMenuItem[];
  onAction: (action: string) => void;
  onClose: () => void;
}

/**
 * The drive's right-click menu. Fixed position, width grows to the widest label
 * (`w-max`), and it never wraps — the items stay one line each.
 */
export function DriveContextMenu({ state, items, onAction, onClose }: DriveContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!state || !ref.current) return;
    const menu = ref.current;
    const rect = menu.getBoundingClientRect();
    const x = Math.min(state.x, window.innerWidth - rect.width - 8);
    const y = Math.min(state.y, window.innerHeight - rect.height - 8);
    menu.style.left = `${Math.max(8, x)}px`;
    menu.style.top = `${Math.max(8, y)}px`;
  }, [state]);

  useEffect(() => {
    if (!state) return;
    const close = () => onClose();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [state, onClose]);

  if (!state) return null;

  return (
    <div
      ref={ref}
      role="menu"
      className="fixed z-50 w-max min-w-40 rounded-lg border border-border-color bg-card p-1 shadow-lg"
      style={{ left: state.x, top: state.y }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {items.map((item, index) => (
        <div key={item.action}>
          {item.separatorBefore && index > 0 && (
            <div className="my-1 h-px bg-border-color" aria-hidden="true" />
          )}
          <button
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              onClose();
              onAction(item.action);
            }}
            className={clsx(
              'flex w-full items-center whitespace-nowrap rounded-md px-3 py-1.5 text-start text-sm transition-colors',
              item.disabled
                ? 'cursor-not-allowed text-muted-foreground opacity-40'
                : item.danger
                  ? 'text-destructive hover:bg-muted'
                  : 'text-foreground hover:bg-muted',
            )}
          >
            {item.label}
          </button>
        </div>
      ))}
    </div>
  );
}

/** The standard file menu, translated once so grid and list render the same set. */
export function driveContextItems(
  hasSelection: boolean,
  t: TFunction<'translation', undefined>,
  isFile = false,
): ContextMenuItem[] {
  return [
    { action: 'open', label: t('drive.open') },
    { action: 'preview', label: t('drive.preview'), disabled: !hasSelection },
    { action: 'download', label: t('drive.download'), disabled: !hasSelection, separatorBefore: true },
    { action: 'share', label: t('drive.share'), disabled: !hasSelection },
    { action: 'versions', label: t('drive.versionsMenu'), disabled: !hasSelection },
    // Files only: the bytes are staged onto the suite shelf so the editors can open them.
    { action: 'sendToEditors', label: t('drive.sendToEditors'), disabled: !hasSelection || !isFile },
    { action: 'rename', label: t('drive.rename'), disabled: !hasSelection, separatorBefore: true },
    { action: 'cut', label: t('drive.cut'), disabled: !hasSelection },
    { action: 'copy', label: t('drive.copy'), disabled: !hasSelection },
    { action: 'delete', label: t('drive.delete'), disabled: !hasSelection, danger: true, separatorBefore: true },
  ];
}
